import React from "react";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { roleManagementService } from "./role-management.service";
import { useToast } from "../../components/ui";
import { TApiServiceError } from "../apiService";
import {
  measureDataFetching,
  handleApiError,
  validateAssignmentRequest,
} from "./role-management.utils";

import {
  TCreateRoleForm,
  TUpdateRoleForm,
  TRemovePermissionsFromRoleRequest,
  TEnhancedRoleListResponse,
  TEnhancedRoleDetailResponse,
  TEnhancedPermissionListResponse,
  TPermissionModulesResponse,
  TBulkPermissionAssignmentRequest,
  TBulkPermissionAssignmentResponse,
  TRoleListFilters,
  TPermissionListFilters,
  TApiRawError,
  TAssignedPermissionListResponse,
  TRoleUsageInfo,
} from "./role-management.types";
import { rootAccessMgmtEndpoint } from "../../core/apiurl";
import { buildQueryParams } from "../../utils/commonUtils";

// ========================================
// ENHANCED SCALABLE INTERFACE HOOKS
// ========================================

// Enhanced Role Management Hooks

/**
 * Hook for fetching enhanced role list with search, filtering, and pagination
 * Supports the new scalable role management interface with optimized caching
 */
// Enhanced permission type for the table
export type TPermissionTableItem = {
  id: number;
  name: string;
  codename: string;
  content_type: {
    id: number;
    app_label: string;
    model: string;
    name: string;
  };
  description?: string;
  is_assigned?: boolean;
  assignment_date?: string;
  assigned_by?: string;
  is_critical?: boolean;
  is_system?: boolean;
};

export const GET_ROLE_LIST_QUERY_KEY = rootAccessMgmtEndpoint;

export const useEnhancedRoleList = (
  filters: TRoleListFilters = {},
  options?: Omit<
    UseQueryOptions<TEnhancedRoleListResponse, Error>,
    "queryKey" | "queryFn"
  > & {
    enabled?: boolean;
    keepPreviousData?: boolean;
  },
) => {
  // Create stable query key that handles pagination properly
  return useQuery({
    queryKey: [GET_ROLE_LIST_QUERY_KEY, "enhanced-role-list", filters],
    queryFn: async () => {
      try {
        measureDataFetching.startFetch("role-list");
        const response =
          await roleManagementService.getEnhancedRoleList(filters);
        measureDataFetching.endFetch(
          "role-list",
          true,
          response.data.results?.length,
        );
        return response.data;
      } catch (error) {
        measureDataFetching.endFetch("role-list", false);
        throw error;
      }
    },
    enabled: options?.enabled ?? true,
    staleTime: 3 * 60 * 1000, // Cache for 3 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    keepPreviousData: options?.keepPreviousData ?? true, // Keep previous data during refetch
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (error?.message?.includes("403") || error?.message?.includes("404")) {
        return false;
      }
      return failureCount < 2; // Reduce retry attempts for better performance
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
    refetchOnMount: false, // Use cached data when available
    ...options,
  });
};

/**
 * Hook for fetching enhanced role detail with comprehensive information
 */
export const useEnhancedRoleDetail = (
  roleId: string,
  options?: Omit<
    UseQueryOptions<TEnhancedRoleDetailResponse, Error>,
    "queryKey" | "queryFn"
  > & {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey: ["enhanced-role-detail", roleId],
    queryFn: async () => {
      const response =
        await roleManagementService.getEnhancedRoleDetail(roleId);
      return response.data;
    },
    enabled: (options?.enabled ?? true) && !!roleId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: (failureCount, error) => {
      if (error?.message?.includes("403") || error?.message?.includes("404")) {
        return false;
      }
      return failureCount < 3;
    },
    ...options,
  });
};

/**
 * Hook for fetching permissions with search, filtering, and role assignment context
 * Optimized for large datasets with intelligent caching and deduplication
 */
export const useEnhancedPermissions = (
  filters: TPermissionListFilters = {},
  options?: Omit<
    UseQueryOptions<TEnhancedPermissionListResponse, Error>,
    "queryKey" | "queryFn"
  > & {
    enabled?: boolean;
    keepPreviousData?: boolean;
  },
) => {
  // Create stable query key with proper pagination handling
  return useQuery({
    queryKey: [GET_ROLE_LIST_QUERY_KEY, "enhanced-permissions", filters],
    queryFn: async () => {
      try {
        measureDataFetching.startFetch("permissions");
        const response =
          await roleManagementService.getEnhancedPermissions(filters);
        measureDataFetching.endFetch(
          "permissions",
          true,
          response.data.results?.length,
        );
        return response.data;
      } catch (error) {
        measureDataFetching.endFetch("permissions", false);
        throw error;
      }
    },
    enabled: options?.enabled ?? true,
    staleTime: 8 * 60 * 1000, // Cache for 8 minutes since permissions change less frequently
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    keepPreviousData: options?.keepPreviousData ?? true, // Smooth transitions during filtering
    retry: (failureCount, error) => {
      if (error?.message?.includes("403") || error?.message?.includes("404")) {
        return false;
      }
      return failureCount < 2; // Reduce retries for better performance
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false, // Permissions rarely change, avoid unnecessary refetches
    refetchOnMount: false, // Use cached data when available
    // Enable request deduplication
    structuralSharing: true,
    ...options,
  });
};

/**
 * Hook for fetching permission modules for filtering interface
 */
export const usePermissionModules = (
  options?: Omit<
    UseQueryOptions<TPermissionModulesResponse, Error>,
    "queryKey" | "queryFn"
  > & {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey: ["permission-modules"],
    queryFn: async () => {
      const response = await roleManagementService.getPermissionModules();
      return response.data;
    },
    enabled: options?.enabled ?? true,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes - modules rarely change
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: (failureCount, error) => {
      if (error?.message?.includes("403") || error?.message?.includes("404")) {
        return false;
      }
      return failureCount < 3;
    },
    ...options,
  });
};

// Enhanced Permission Assignment Mutations

/**
 * Hook for bulk permission assignment operations
 */
export const useBulkPermissionAssignment = (
  options?: Omit<
    UseMutationOptions<
      TBulkPermissionAssignmentResponse,
      TApiServiceError,
      TBulkPermissionAssignmentRequest
    >,
    "mutationFn"
  >,
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: TBulkPermissionAssignmentRequest) => {
      // Validate request data
      const validation = validateAssignmentRequest(data);
      if (!validation.valid) {
        throw new Error(`Invalid request: ${validation.errors.join(", ")}`);
      }

      try {
        const response =
          await roleManagementService.bulkAssignPermissions(data);
        return response.data;
      } catch (error) {
        const errorMessage = handleApiError(error as TApiRawError);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (response, variables) => {
      // Invalidate relevant queries
      void queryClient.invalidateQueries({ queryKey: ["enhanced-role-list"] });
      void queryClient.invalidateQueries({
        queryKey: ["enhanced-role-detail", variables.role_id],
      });
      void queryClient.invalidateQueries({
        queryKey: ["enhanced-permissions"],
        predicate: (query) => {
          const filters = query.queryKey[1] as TPermissionListFilters;
          return filters?.role_id === variables.role_id;
        },
      });

      // NEW — Assigned permissions
      void queryClient.invalidateQueries({
        queryKey: ["assigned-permissions", variables.role_id],
      });

      // NEW — Available permissions
      void queryClient.invalidateQueries({
        queryKey: ["available-permissions", variables.role_id],
      });

      // NEW — KPI sync
      void queryClient.invalidateQueries({
        queryKey: ["permission-kpi", variables.role_id],
      });

      // Show success message with details
      const actionText = variables.action === "assign" ? "assigned" : "removed";
      const successCount = response.successful_operations;
      const failedCount = response.failed_operations;

      if (failedCount === 0) {
        toast({
          title: `Permissions ${actionText} successfully.`,
          description: `${successCount} permissions were ${actionText}.`,
          variant: "green",
        });
      } else {
        toast({
          title: `Permissions ${actionText} with some issues.`,
          description: `${successCount} permissions ${actionText}, ${failedCount} failed.`,
          variant: "yellow",
        });
      }
    },
    onError: (error: TApiServiceError, variables) => {
      const actionText = variables.action === "assign" ? "assign" : "remove";
      toast({
        title: `Failed to ${actionText} permissions.`,
        description:
          error?.message ??
          `An error occurred while trying to ${actionText} permissions.`,
        variant: "destructive",
      });
    },
    ...options,
  });
};

/**
 * Hook for optimistic permission toggle (single permission assignment/removal)
 */
export const usePermissionToggle = (
  options?: Omit<
    UseMutationOptions<
      TBulkPermissionAssignmentResponse,
      TApiServiceError,
      { roleId: string; permissionId: string; isAssigned: boolean },
      { previousPermissions: [readonly unknown[], unknown][] }
    >,
    "mutationFn"
  >,
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    TBulkPermissionAssignmentResponse,
    TApiServiceError,
    { roleId: string; permissionId: string; isAssigned: boolean },
    { previousPermissions: [readonly unknown[], unknown][] }
  >({
    mutationFn: async ({ roleId, permissionId, isAssigned }) => {
      try {
        const action = isAssigned ? "remove" : "assign";
        const response = await roleManagementService.bulkAssignPermissions({
          role_id: roleId,
          permission_ids: [permissionId],
          action,
        });
        return response.data;
      } catch (error) {
        throw error as TApiServiceError;
      }
    },

    onMutate: async ({ permissionId, isAssigned }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["enhanced-permissions"] });

      // Snapshot the previous value
      const previousPermissions = queryClient.getQueriesData({
        queryKey: ["enhanced-permissions"],
      });

      // Optimistically update the cache
      queryClient.setQueryData(
        ["enhanced-permissions"],
        (old: TEnhancedPermissionListResponse | undefined) => {
          if (!old) return old;

          return {
            ...old,
            results: old.results.map((permission) =>
              permission.internal_id === permissionId
                ? { ...permission, is_assigned: !isAssigned }
                : permission,
            ),
            assignment_context: old.assignment_context
              ? {
                  ...old.assignment_context,
                  assigned_count: isAssigned
                    ? old.assignment_context.assigned_count - 1
                    : old.assignment_context.assigned_count + 1,
                  available_count: isAssigned
                    ? old.assignment_context.available_count + 1
                    : old.assignment_context.available_count - 1,
                }
              : undefined,
          };
        },
      );

      return { previousPermissions };
    },

    onError: (error: TApiServiceError, variables, context) => {
      // Rollback optimistic update
      if (context?.previousPermissions) {
        context.previousPermissions.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      const actionText = variables.isAssigned ? "remove" : "assign";
      toast({
        title: `Failed to ${actionText} permission.`,
        description:
          error?.message ??
          `An error occurred while trying to ${actionText} the permission.`,
        variant: "destructive",
      });
    },

    onSuccess: (_response, variables) => {
      // Invalidate queries to ensure consistency
      void queryClient.invalidateQueries({
        queryKey: ["enhanced-role-detail", variables.roleId],
      });
      void queryClient.invalidateQueries({ queryKey: ["enhanced-role-list"] });

      // NEW — Assigned permissions
      void queryClient.invalidateQueries({
        queryKey: ["assigned-permissions", variables.roleId],
      });

      // NEW — Available permissions
      void queryClient.invalidateQueries({
        queryKey: ["available-permissions", variables.roleId],
      });

      // NEW — KPI sync
      void queryClient.invalidateQueries({
        queryKey: ["permission-kpi", variables.roleId],
      });
    },
    onSettled: () => {
      // Always refetch after error or success
      void queryClient.invalidateQueries({
        queryKey: ["enhanced-permissions"],
      });
    },

    ...options,
  });
};

// Enhanced Data Management Hooks with Debouncing

/**
 * Advanced debouncing hook with different delays for different operations
 */
export const useAdvancedDebounce = <T>(
  value: T,
  delay: number,
  options?: {
    leading?: boolean;
    trailing?: boolean;
    maxWait?: number;
  },
) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  const timeoutRef = React.useRef<NodeJS.Timeout>();
  const maxTimeoutRef = React.useRef<NodeJS.Timeout>();
  const lastCallTimeRef = React.useRef<number>();

  React.useEffect(() => {
    const now = Date.now();
    const timeSinceLastCall = lastCallTimeRef.current
      ? now - lastCallTimeRef.current
      : 0;

    const callNow = options?.leading && !timeoutRef.current;

    if (callNow) {
      setDebouncedValue(value);
    }

    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
    }

    // Set up trailing call
    if (options?.trailing !== false) {
      timeoutRef.current = setTimeout(() => {
        setDebouncedValue(value);
        timeoutRef.current = undefined;
        lastCallTimeRef.current = Date.now();
      }, delay);
    }

    // Set up max wait timeout
    if (options?.maxWait && timeSinceLastCall < options.maxWait) {
      maxTimeoutRef.current = setTimeout(() => {
        setDebouncedValue(value);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = undefined;
        }
        lastCallTimeRef.current = Date.now();
      }, options.maxWait - timeSinceLastCall);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (maxTimeoutRef.current) {
        clearTimeout(maxTimeoutRef.current);
      }
    };
  }, [value, delay, options?.leading, options?.trailing, options?.maxWait]);

  return debouncedValue;
};

/**
 * Hook for debounced role search with intelligent delay adjustment
 */
export const useDebouncedRoleSearch = (
  searchTerm: string,
  additionalFilters: Omit<TRoleListFilters, "search"> = {},
  options?: {
    debounceMs?: number;
    minSearchLength?: number;
  },
) => {
  const debounceMs = options?.debounceMs ?? 300;
  const minSearchLength = options?.minSearchLength ?? 2;

  // Use shorter debounce for longer search terms (user is more likely to be done typing)
  const adaptiveDelay = searchTerm.length > 5 ? debounceMs * 0.7 : debounceMs;

  const debouncedSearchTerm = useAdvancedDebounce(searchTerm, adaptiveDelay, {
    leading: false,
    trailing: true,
    maxWait: debounceMs * 2, // Ensure we don't wait too long
  });

  const hasValidSearch = debouncedSearchTerm.length >= minSearchLength;
  const hasFilters = Object.keys(additionalFilters).length > 0;

  return useEnhancedRoleList(
    {
      search: debouncedSearchTerm,
      ...additionalFilters,
    },
    {
      enabled: hasValidSearch || hasFilters,
      keepPreviousData: true,
    },
  );
};

/**
 * Hook for debounced permission search with optimized performance
 */
// export const useDebouncedPermissionSearch = (
//   searchTerm: string,
//   additionalFilters: Omit<TPermissionListFilters, 'search'> = {},
//   options?: {
//     debounceMs?: number;
//     minSearchLength?: number;
//     useInfiniteScroll?: boolean;
//   }
// ) => {
//   const debounceMs = options?.debounceMs ?? 300;
//   const minSearchLength = options?.minSearchLength ?? 2;
//   const useInfiniteScroll = options?.useInfiniteScroll ?? false;

//   // Adaptive debouncing based on search term characteristics
//   const adaptiveDelay = React.useMemo(() => {
//     if (searchTerm.length === 0) return 0; // Immediate clear
//     if (searchTerm.length > 8) return debounceMs * 0.6; // Faster for longer terms
//     if (searchTerm.includes(' ')) return debounceMs * 0.8; // Faster for multi-word
//     return debounceMs;
//   }, [searchTerm, debounceMs]);

//   const debouncedSearchTerm = useAdvancedDebounce(searchTerm, adaptiveDelay, {
//     leading: false,
//     trailing: true,
//     maxWait: debounceMs * 1.5,
//   });

//   const hasValidSearch = debouncedSearchTerm.length >= minSearchLength;
//   const hasFilters = Object.keys(additionalFilters).length > 0;

//   // Use infinite scroll hook if requested
//   if (useInfiniteScroll) {
//     return useInfinitePermissions({
//       search: debouncedSearchTerm,
//       ...additionalFilters,
//     });
//   }

//   return useEnhancedPermissions({
//     search: debouncedSearchTerm,
//     ...additionalFilters,
//   }, {
//     enabled: hasValidSearch || hasFilters,
//     keepPreviousData: true,
//   });
// };

export const useDebouncedPermissionSearch = (
  searchTerm: string,
  additionalFilters: Omit<TPermissionListFilters, "search"> = {},
  options?: {
    debounceMs?: number;
    minSearchLength?: number;
    useInfiniteScroll?: boolean;
  },
) => {
  const debounceMs = options?.debounceMs ?? 300;
  const minSearchLength = options?.minSearchLength ?? 2;
  const useInfiniteScroll = options?.useInfiniteScroll ?? false;

  const adaptiveDelay = React.useMemo(() => {
    if (searchTerm.length === 0) return 0;
    if (searchTerm.length > 8) return debounceMs * 0.6;
    if (searchTerm.includes(" ")) return debounceMs * 0.8;
    return debounceMs;
  }, [searchTerm, debounceMs]);

  const debouncedSearchTerm = useAdvancedDebounce(searchTerm, adaptiveDelay, {
    leading: false,
    trailing: true,
    maxWait: debounceMs * 1.5,
  });

  const hasValidSearch = debouncedSearchTerm.length >= minSearchLength;
  const hasFilters = Object.keys(additionalFilters).length > 0;

  // **Call both hooks unconditionally**
  const infinitePermissions = useInfinitePermissions({
    search: debouncedSearchTerm,
    ...additionalFilters,
  });

  const enhancedPermissions = useEnhancedPermissions(
    {
      search: debouncedSearchTerm,
      ...additionalFilters,
    },
    {
      enabled: hasValidSearch || hasFilters,
      keepPreviousData: true,
    },
  );

  // **Return the one you want**
  return useInfiniteScroll ? infinitePermissions : enhancedPermissions;
};

/**
 * Hook for throttled filter changes to prevent excessive API calls
 */
export const useThrottledFilters = <T extends Record<string, any>>(
  filters: T,
  throttleMs = 500,
) => {
  const [throttledFilters, setThrottledFilters] = React.useState(filters);
  const lastUpdateRef = React.useRef<number>(0);
  const timeoutRef = React.useRef<NodeJS.Timeout>();

  React.useEffect(() => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateRef.current;

    if (timeSinceLastUpdate >= throttleMs) {
      // Update immediately if enough time has passed
      setThrottledFilters(filters);
      lastUpdateRef.current = now;
    } else {
      // Schedule update for later
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setThrottledFilters(filters);
        lastUpdateRef.current = Date.now();
      }, throttleMs - timeSinceLastUpdate);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [filters, throttleMs]);

  return throttledFilters;
};

/**
 * Hook for managing role selection state with URL synchronization
 */
export const useRoleSelection = (initialRoleId?: string) => {
  const [selectedRoleId, setSelectedRoleId] = React.useState<string | null>(
    initialRoleId ?? null,
  );

  const roleDetailQuery = useEnhancedRoleDetail(selectedRoleId ?? "", {
    enabled: !!selectedRoleId,
  });

  const selectRole = React.useCallback((roleId: string) => {
    setSelectedRoleId(roleId);
  }, []);

  const clearSelection = React.useCallback(() => {
    setSelectedRoleId(null);
  }, []);

  return {
    selectedRoleId,
    selectedRole: roleDetailQuery.data,
    isLoadingRole: roleDetailQuery.isLoading,
    roleError: roleDetailQuery.error,
    selectRole,
    clearSelection,
    refetchRole: roleDetailQuery.refetch,
  };
};

/**
 * Hook for infinite scrolling permissions with prefetching
 */
export const useInfinitePermissions = (
  baseFilters: Omit<TPermissionListFilters, "page" | "page_size"> = {},
  pageSize = 50,
) => {
  const queryClient = useQueryClient();

  const [allPermissions, setAllPermissions] = React.useState<
    TPermissionTableItem[]
  >([]);
  const [hasMore, setHasMore] = React.useState(true);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);

  // Main query for current page
  const permissionsQuery = useEnhancedPermissions({
    ...baseFilters,
    page: currentPage,
    page_size: pageSize,
  });

  // Update accumulated permissions when new data arrives
  React.useEffect(() => {
    if (permissionsQuery.data?.results) {
      if (currentPage === 1) {
        // Reset for new search/filter
        setAllPermissions(permissionsQuery.data.results);
      } else {
        // Append new page data
        setAllPermissions((prev) => [
          ...prev,
          ...permissionsQuery.data.results,
        ]);
      }

      // Check if there are more pages
      setHasMore(!!permissionsQuery.data.next);
      setIsLoadingMore(false);
    }
  }, [permissionsQuery.data, currentPage]);

  // Step 1: memoize the string
  const baseFiltersString = React.useMemo(
    () => JSON.stringify(baseFilters),
    [baseFilters],
  );

  // Step 2: use in useEffect
  React.useEffect(() => {
    setCurrentPage(1);
    setAllPermissions([]);
    setHasMore(true);
  }, [baseFiltersString]);

  // Load more function
  const loadMore = React.useCallback(() => {
    if (hasMore && !permissionsQuery.isLoading && !isLoadingMore) {
      setIsLoadingMore(true);
      setCurrentPage((prev) => prev + 1);

      // Prefetch next page
      const nextPage = currentPage + 2;
      void queryClient.prefetchQuery({
        queryKey: [
          "enhanced-permissions",
          baseFilters,
          { page: nextPage, page_size: pageSize },
        ],
        queryFn: async () => {
          const response = await roleManagementService.getEnhancedPermissions({
            ...baseFilters,
            page: nextPage,
            page_size: pageSize,
          });
          return response.data;
        },
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [
    hasMore,
    permissionsQuery.isLoading,
    isLoadingMore,
    currentPage,
    baseFilters,
    pageSize,
    queryClient,
  ]);

  return {
    permissions: allPermissions,
    isLoading: permissionsQuery.isLoading && currentPage === 1,
    isLoadingMore,
    hasMore,
    loadMore,
    error: permissionsQuery.error,
    totalCount: permissionsQuery.data?.count ?? 0,
    assignmentContext: permissionsQuery.data?.assignment_context,
    refetch: () => {
      setCurrentPage(1);
      setAllPermissions([]);
      return permissionsQuery.refetch();
    },
  };
};

/**
 * Hook for prefetching role data based on user interaction patterns
 */
export const useRolePrefetching = () => {
  const queryClient = useQueryClient();

  const prefetchRoleDetail = React.useCallback(
    (roleId: string) => {
      void queryClient.prefetchQuery({
        queryKey: ["enhanced-role-detail", roleId],
        queryFn: async () => {
          const response =
            await roleManagementService.getEnhancedRoleDetail(roleId);
          return response.data;
        },
        staleTime: 5 * 60 * 1000,
      });
    },
    [queryClient],
  );

  const prefetchRolePermissions = React.useCallback(
    (roleId: string) => {
      void queryClient.prefetchQuery({
        queryKey: [
          "enhanced-permissions",
          { role_id: roleId },
          { page: 1, page_size: 50 },
        ],
        queryFn: async () => {
          const response = await roleManagementService.getEnhancedPermissions({
            role_id: roleId,
            page: 1,
            page_size: 50,
          });
          return response.data;
        },
        staleTime: 5 * 60 * 1000,
      });
    },
    [queryClient],
  );

  const prefetchNextRolePage = React.useCallback(
    (currentFilters: TRoleListFilters) => {
      const nextPage = (currentFilters.page ?? 1) + 1;
      void queryClient.prefetchQuery({
        queryKey: ["enhanced-role-list", { ...currentFilters, page: nextPage }],
        queryFn: async () => {
          const response = await roleManagementService.getEnhancedRoleList({
            ...currentFilters,
            page: nextPage,
          });
          return response.data;
        },
        staleTime: 3 * 60 * 1000,
      });
    },
    [queryClient],
  );

  return {
    prefetchRoleDetail,
    prefetchRolePermissions,
    prefetchNextRolePage,
  };
};

/**
 * Hook for managing permission filters state
 */
export const usePermissionFilters = (
  initialFilters: TPermissionListFilters = {},
) => {
  const [filters, setFilters] =
    React.useState<TPermissionListFilters>(initialFilters);

  const updateFilter = React.useCallback(
    <K extends keyof TPermissionListFilters>(
      key: K,
      value: TPermissionListFilters[K],
    ) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
        page: 1, // Reset to first page when filters change
      }));
    },
    [],
  );

  const clearFilters = React.useCallback(() => {
    setFilters({ page: 1, page_size: filters.page_size });
  }, [filters.page_size]);

  const resetToInitial = React.useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  return {
    filters,
    updateFilter,
    clearFilters,
    resetToInitial,
    setFilters,
  };
};

/**
 * Hook for managing role filters state
 */
export const useRoleFilters = (initialFilters: TRoleListFilters = {}) => {
  const [filters, setFilters] =
    React.useState<TRoleListFilters>(initialFilters);

  const updateFilter = React.useCallback(
    <K extends keyof TRoleListFilters>(key: K, value: TRoleListFilters[K]) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
        page: 1, // Reset to first page when filters change
      }));
    },
    [],
  );

  const clearFilters = React.useCallback(() => {
    setFilters({ page: 1, page_size: filters.page_size });
  }, [filters.page_size]);

  const resetToInitial = React.useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  return {
    filters,
    updateFilter,
    clearFilters,
    resetToInitial,
    setFilters,
  };
};

// ========================================
// LEGACY HOOKS (Backward Compatibility)
// ========================================

// Role Queries
export const useGetRoleListQuery = (
  query = "",
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey: ["role-list", query],
    queryFn: async () => {
      const response = await roleManagementService.getRoleList(query);
      return response.data;
    },
    enabled: options?.enabled ?? true,
  });
};

export const useGetRoleByIdQuery = (
  id: string,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery<TEnhancedRoleDetailResponse>({
    queryKey: ["role-detail", id],
    queryFn: async () => {
      const response = await roleManagementService.getRoleById(id);
      return response.data;
    },
    enabled: options?.enabled ?? !!id,
  });
};

export const useGetRoleUsageInfoQuery = (
  id: number,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery<TRoleUsageInfo>({
    queryKey: ["role-usage-info", id],
    queryFn: async () => {
      const response = await roleManagementService.getRoleUsageInfo(id);
      return response.data;
    },
    enabled: options?.enabled ?? !!id,
  });
};

// Role Mutations
export const useCreateRoleMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, TCreateRoleForm>,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: async (data: TCreateRoleForm) => {
      try {
        const response = await roleManagementService.createRole(data);
        return response.data;
      } catch (error) {
        throw error as TApiServiceError;
      }
    },
    ...options,
  });
};

export const useUpdateRoleMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, TUpdateRoleForm>,
    "mutationFn"
  >,
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: TUpdateRoleForm) => {
      try {
        const response = await roleManagementService.updateRole(data);
        return response;
      } catch (error) {
        throw error as TApiServiceError;
      }
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["role-list"] });
      void queryClient.invalidateQueries({
        queryKey: ["role-detail", variables.internal_id],
      });
      toast({
        title: "Role updated successfully.",
        variant: "green",
      });
    },
    onError: (error: TApiServiceError) => {
      toast({
        title: error?.message ?? "Failed to update role.",
        variant: "destructive",
      });
    },
    ...options,
  });
};

export const useDeleteRoleMutation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (internalId: string) => {
      try {
        const response = await roleManagementService.deleteRole(internalId);
        return response;
      } catch (error) {
        throw error as TApiServiceError;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["role-list"] });
      toast({
        title: "Role deleted successfully.",
        variant: "green",
      });
    },
    onError: (error: TApiServiceError) => {
      toast({
        title: error?.message ?? "Failed to delete role.",
        variant: "destructive",
      });
    },
  });
};

export const useRemovePermissionsFromRoleMutation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: TRemovePermissionsFromRoleRequest) =>
      roleManagementService.removePermissionsFromRole(data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["role-permissions", variables.role_id],
      });
      void queryClient.invalidateQueries({
        queryKey: ["role-detail", variables.role_id],
      });
      void queryClient.invalidateQueries({ queryKey: ["role-list"] });
      toast({
        title: "Permissions removed successfully.",
        variant: "green",
      });
    },
    onError: (error: TApiServiceError) => {
      toast({
        title: error?.message ?? "Failed to remove permissions.",
        variant: "destructive",
      });
    },
  });
};

// Bulk Operations
export const useBulkUpdateRoleStatusMutation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      roleIds,
      isActive,
    }: {
      roleIds: number[];
      isActive: boolean;
    }) => roleManagementService.bulkUpdateRoleStatus(roleIds, isActive),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["role-list"] });
      toast({
        title: "Roles updated successfully.",
        variant: "green",
      });
    },
    onError: (error: TApiServiceError) => {
      toast({
        title: error?.message ?? "Failed to update roles.",
        variant: "destructive",
      });
    },
  });
};

// Permission KPI
export const useGetPermissionKPIQuery = (
  roleId: string,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey: ["permission-kpi", roleId],
    queryFn: async () => {
      return await roleManagementService.getPermissionKPI(roleId);
    },
    enabled: options?.enabled ?? !!roleId,
  });
};

/**
 * Hook for fetching ASSIGNED permissions for a role
 */

export const useAssignedPermissions = (
  roleId: string,
  filters: TPermissionListFilters = {},
  options?: Omit<
    UseQueryOptions<TAssignedPermissionListResponse, Error>,
    "queryKey" | "queryFn"
  > & {
    enabled?: boolean;
    keepPreviousData: true;
  },
) => {
  return useQuery({
    queryKey: [
      GET_ROLE_LIST_QUERY_KEY,
      "assigned-permissions",
      roleId,
      filters,
    ],
    queryFn: async () => {
      const query = buildQueryParams(filters);
      const response = await roleManagementService.getAssignedPermissions(
        roleId,
        query,
      );
      return response.data;
    },
    enabled: (options?.enabled ?? true) && !!roleId,
    keepPreviousData: options?.keepPreviousData ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

/**
 * Hook for fetching AVAILABLE permissions for a role
 */
export const useAvailablePermissions = (
  roleId: string,
  filters: TPermissionListFilters = {},
  options?: Omit<
    UseQueryOptions<TAssignedPermissionListResponse, Error>,
    "queryKey" | "queryFn"
  > & {
    enabled?: boolean;
    keepPreviousData?: true;
  },
) => {
  return useQuery({
    queryKey: [
      GET_ROLE_LIST_QUERY_KEY,
      "available-permissions",
      roleId,
      filters,
    ],
    queryFn: async () => {
      const query = buildQueryParams(filters);
      const response = await roleManagementService.getAvailablePermissions(
        roleId,
        query,
      );
      return response.data;
    },
    enabled: (options?.enabled ?? true) && !!roleId,
    keepPreviousData: options?.keepPreviousData ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useRoleHierarchyPreviewMutation = () => {
  return useMutation({
    mutationFn: async (payload: {
      hierarchy_placement?: import("./role-management.types").THierarchyPlacement;
      role_internal_id?: string;
      new_role_name?: string;
    }) => {
      const response =
        await roleManagementService.previewRoleHierarchy(payload);
      return response.data;
    },
  });
};
